import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common';
import {
  Alert, Button, Card, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { repairPayload, validatePage } from '../common/courierSettlementView';

const money = value => typeof value === 'number' && Number.isFinite(value) ? `${value.toLocaleString('tr-TR')} TL` : 'Doğrulanmadı';
const reasons = {
  'original-entitlement-review-required': 'Orijinal hakediş kanıtı yok; tutar elle varsayılamaz.',
  'booking-not-complete': 'Sipariş henüz tamamlanmamış.',
  'booking-not-found': 'Sipariş bulunamadı.',
  'preview-required': 'Ayrıntılı kontrol gerekli.',
  'preview-stale': 'Önizlemeden sonra kayıt değişmiş; tekrar kontrol edin.',
  'settlement-conflict': 'Çelişen bir bakiye hareketi var; elle inceleme gerekli.',
  'manual-review-required': 'Kayıt güvenli otomatik düzeltmeye uygun değil; elle inceleme gerekli.',
  'evidence-unavailable': 'Kanıtlar okunamadı. Bu, ödeme olmadığı anlamına gelmez.',
  'evidence-limit-exceeded': 'Kontrol sınırına ulaşıldı; sonuç kesinleştirilemedi.',
  'repair-disabled': 'Düzeltme henüz sunucuda etkin değil.',
  'shared-payout-owner-required': 'Ortak ödeme koordinasyonu henüz doğrulanmadı.',
};
const describe = code => reasons[code] || String(code || 'Bilinmeyen durum');
const states = { RESOLVED: 'İşlem kaydı tamamlanmış', PROCESSING: 'İşleniyor', REVIEW_REQUIRED: 'Kanıt kontrolü gerekli', BLOCKED: 'Düzeltme engelli' };

export default function CourierSettlements() {
  const dispatch = useDispatch();
  const settings = useSelector(state => state.settingsdata.settings);
  const [page, setPage] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [previous, setPrevious] = useState([]);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [bookingId, setBookingId] = useState('');
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const run = useCallback(async action => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError('');
    try { await action(); }
    catch (failure) { if (mounted.current) setError(describe(failure.code) !== 'Bilinmeyen durum' && failure.code ? describe(failure.code) : failure.message || 'İşlem tamamlanamadı.'); }
    finally { inFlight.current = false; if (mounted.current) setBusy(false); }
  }, []);

  const load = useCallback(nextCursor => run(async () => {
    setPreview(null); setPage(null);
    const result = validatePage(await dispatch(api.fetchCourierSettlements({ cursor: nextCursor, limit: 25 })));
    if (mounted.current) setPage(result);
  }), [dispatch, run]);
  useEffect(() => { load(cursor); }, [cursor, load]);

  const inspect = id => run(async () => {
    setPreview(null); setNote(''); setNotice('');
    const result = await dispatch(api.previewCourierSettlement({ bookingId: id }));
    if (result?.success !== true || result.bookingId !== id || !Array.isArray(result.blockers) || !Array.isArray(result.legs)) throw new Error('Önizleme yanıtı doğrulanamadı.');
    if (mounted.current) setPreview(result);
  });

  const repair = () => run(async () => {
    setConfirm(false);
    if (!settings?.AllowCriticalEditsAdmin) throw new Error('Kritik yönetici işlemleri kapalı.');
    const payload = repairPayload(preview, note);
    // Discard the preview even after a disconnect. The next action must inspect
    // the server's canonical markers; it never assumes the last request failed.
    setPreview(null); setNotice('');
    const result = await dispatch(api.repairCourierSettlement(payload));
    if (result?.success !== true || !['repaired', 'already-settled'].includes(result.state)) throw new Error('Sonuç doğrulanamadı. Yeni bir bakiye işlemi yapmadan siparişi tekrar kontrol edin.');
    if (mounted.current) setNotice(result.state === 'already-settled'
      ? 'Hareketler zaten mevcut; ek bakiye hareketi oluşturulmadı.'
      : 'Eksik hareketler tamamlandı. Güncel durumu tekrar kontrol edebilirsiniz.');
    const updated = validatePage(await dispatch(api.fetchCourierSettlements({ cursor, limit: 25 })));
    if (mounted.current) setPage(updated);
  });

  let canRepair = false;
  try { repairPayload(preview, note); canRepair = !!settings?.AllowCriticalEditsAdmin; } catch (_) { /* Incomplete previews are intentionally read-only. */ }

  return <Card sx={{ p: 3, borderRadius: 3 }}>
    <Typography variant="h5" component="h1" gutterBottom>Kurye Hakediş Kontrolü</Typography>
    <Alert severity="info" sx={{ mb: 2 }}>Liste ve önizleme bakiyeyi değiştirmez. Bir sayfada en fazla 25 sipariş taranır; bu liste tüm geçmişin eksiksiz kontrol edildiği anlamına gelmez. Kayıttaki tutar, orijinal hakediş kanıtı değildir.</Alert>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
      <TextField label="Sipariş kayıt kimliği" value={bookingId} onChange={event => setBookingId(event.target.value)} size="small" disabled={busy} helperText="Veritabanındaki sipariş anahtarı" />
      <Button disabled={busy || !bookingId.trim()} onClick={() => inspect(bookingId.trim())}>Siparişi kontrol et</Button>
      <Button disabled={busy} onClick={() => load(cursor)}>Listeyi yenile</Button>
    </div>
    {busy && <div role="status" aria-label="Kontrol ediliyor"><CircularProgress size={24} /> Kontrol ediliyor…</div>}
    {page && <>
      <Typography variant="body2">Bu sayfada taranan: {page.scannedCount} · İncelemeye sunulan: {page.items.length}</Typography>
      <TableContainer><Table size="small" aria-label="Kurye hakediş kayıtları">
        <TableHead><TableRow><TableCell>Sipariş</TableCell><TableCell>Durum / ödeme</TableCell><TableCell>Kayıttaki hakediş</TableCell><TableCell>Kanıt durumu</TableCell><TableCell>İşlem</TableCell></TableRow></TableHead>
        <TableBody>{page.items.map(row => <TableRow key={row.bookingId}>
          <TableCell>{row.reference || row.bookingId}</TableCell>
          <TableCell>{row.status} / {row.paymentMode}</TableCell>
          <TableCell>{money(row.driverShare)}<Typography variant="caption" display="block">Bilgi amaçlı; kesin alacak değildir</Typography></TableCell>
          <TableCell>{states[row.settlementState] || row.settlementState}{(row.reasonCodes || []).map(reason => <Typography key={reason} variant="caption" display="block">{describe(reason)}</Typography>)}</TableCell>
          <TableCell><Button disabled={busy} onClick={() => inspect(row.bookingId)}>Önizle</Button></TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer>
      {!page.items.length && <Typography sx={{ my: 2 }}>Bu sayfada uygun kayıt bulunmadı.{page.hasMore ? ' Sonraki sayfada tarama devam eder.' : ''}</Typography>}
      <Button disabled={busy || !previous.length} onClick={() => { const history = previous.slice(); setCursor(history.pop()); setPrevious(history); }}>Önceki sayfa</Button>
      <Button disabled={busy || !page.hasMore} onClick={() => { setPrevious([...previous, cursor]); setCursor(page.nextCursor); }}>Sonraki sayfa</Button>
    </>}
    {preview && <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Typography variant="h6">Önizleme: {preview.bookingId}</Typography>
      <Typography>Durum: {states[preview.state] || preview.state}</Typography>
      {preview.blockers.map(code => <Alert key={code} severity="warning" sx={{ mt: 1 }}>{describe(code)}</Alert>)}
      <TableContainer><Table size="small" aria-label="Doğrulanan bakiye hareketleri">
        <TableHead><TableRow><TableCell>Alıcı hesap</TableCell><TableCell>İşlem</TableCell><TableCell>Tutar</TableCell><TableCell>Kanıt</TableCell></TableRow></TableHead>
        <TableBody>{preview.legs.map((leg, index) => <TableRow key={leg.canonicalKey || index}>
          <TableCell>{leg.uid || leg.recipientUid || 'Doğrulanmadı'}</TableCell><TableCell>{leg.operation === 'debit' ? 'Düşüm' : leg.operation === 'credit' ? 'Ekleme' : 'Doğrulanmadı'}</TableCell><TableCell>{money(leg.amount)}</TableCell><TableCell>{leg.state || leg.observed || 'Doğrulanmadı'}</TableCell>
        </TableRow>)}</TableBody>
      </Table></TableContainer>
      <TextField fullWidth multiline minRows={2} label="Denetim açıklaması (zorunlu)" value={note} onChange={event => setNote(event.target.value)} inputProps={{ maxLength: 1000 }} disabled={busy || !preview.repairEligible} sx={{ mt: 2 }} />
      <Button variant="contained" disabled={busy || !canRepair} onClick={() => setConfirm(true)} sx={{ mt: 2 }}>Eksik hareketleri tamamla</Button>
      <Button disabled={busy} onClick={() => inspect(preview.bookingId)} sx={{ mt: 2, ml: 1 }}>Önizlemeyi yenile</Button>
    </Card>}
    <Dialog open={confirm} onClose={() => { if (!busy) setConfirm(false); }}>
      <DialogTitle>Kurye hareketlerini tamamlamak istiyor musunuz?</DialogTitle>
      <DialogContent>Yalnızca sunucunun doğruladığı eksik hareketler uygulanacaktır. Mevcut hareketler tekrar işlenmez. Müşteriye iade yapılmaz ve sipariş durumu değiştirilmez.</DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setConfirm(false)}>Vazgeç</Button><Button disabled={busy || !canRepair} onClick={repair}>Onayla ve tamamla</Button></DialogActions>
    </Dialog>
  </Card>;
}
